import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isBlank } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import t from 'ember-intl/helpers/t';
import PowerSelect, { type Select } from 'ember-power-select/components/power-select';
import { not } from 'ember-truth-helpers';
import fetch from 'fetch';
import NoResults from 'invite-sign/components/mapped-field/host-field/no-results';
import config from 'invite-sign/config/environment';
import type SignInField from 'invite-sign/models/sign-in-field';
import type SessionService from 'invite-sign/services/session';

const DEBOUNCE_MS = 250;

interface HostFieldSignature {
  Args: {
    id: string;
    mappedField: SignInField;
  };
}

export default class HostField extends Component<HostFieldSignature> {
  @service declare session: SessionService;

  @tracked totalResults = 0;
  @tracked _EPSapi!: Select;

  @action
  registerPowerSelectAPI(publicAPI: Select) {
    this._EPSapi = publicAPI;
  }

  searchHostTask = task(
    {
      restartable: true,
    },
    async (query): Promise<string[]> => {
      let employees: Array<{ name: string }> = [];

      if (isBlank(query) || query.length < 3) {
        this.totalResults = 0;
        return [];
      }
      // Pause here for DEBOUNCE_MS milliseconds. Because this
      // task is `restartable`, if the user starts typing again,
      // the current search will be canceled at this point and
      // start over from the beginning.
      await timeout(DEBOUNCE_MS);
      const { token_type, access_token } = this.session.sessionData!;
      let filter = `filter[query]=${query}`;
      let response = await fetch(`${config.apiHost}/api/v3/host-names?${filter}`, {
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          Authorization: `${token_type} ${access_token}`,
        },
        method: 'GET',
      });
      if (response.ok) {
        let { data } = await response.json();
        employees = data[0].attributes.employees;
      }
      this.totalResults = employees.length;
      return employees.map(({ name }) => name);
    },
  );

  <template>
    <PowerSelect
      @search={{this.searchHostTask.perform}}
      @searchEnabled={{true}}
      @placeholder={{t "choose_host"}}
      @noMatchesMessage=""
      @searchMessage={{t "type_to_search"}}
      @loadingMessage={{t "loading_eps"}}
      @registerAPI={{this.registerPowerSelectAPI}}
      @afterOptionsComponent={{component
        NoResults
        totalResults=this.totalResults
        searchName=this._EPSapi.searchText
        loading=this.searchHostTask.isRunning
      }}
      @selected={{@mappedField.value}}
      @allowClear={{not @mappedField.required}}
      @onChange={{fn (mut @mappedField.value)}}
      @disabled={{@mappedField.isDisabled}}
      as |employeeName|
    >
      {{! data-private: employee directory names render in a body-wormholed dropdown, so tag the option content itself (covers both the dropdown list and the selected value in the trigger) }}
      <span data-private>{{employeeName}}</span>
    </PowerSelect>
  </template>
}
